// Ficha de una tienda en Comisiones pagadas: lo pagado por lo que se vendió ahí, su costo por
// semana y sus vendedores esta semana.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { CopyableId } from '@/kit';
import { formatMoneyCents, formatShortMXN } from '@/shared/lib/format/money';
import { useLivePayout } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import {
    contributorsOf,
    sellersAtLeastTitle,
    sellersChip,
    type StorePaidRow,
} from '../../lib/paidRows';
import { sharePct } from '../../lib/payoutRows';
import { SECTION_TITLE } from '../fieldStyles';
import { ContributorsList } from './ContributorsList';
import { SeriesSection } from './SeriesSection';

const CHIP = 'rounded-full border border-border px-2 py-0.5 text-[10.5px]';

interface StorePaidDetailProps {
    row: StorePaidRow;
    totalCents: number;
    when: string;
    directory: CommissionDirectory;
    /** Qué tanto cuadra la lista de quién aportó con la fila. */
    contributorsNote: string | null;
}

export function StorePaidDetail({
    row,
    totalCents,
    when,
    directory,
    contributorsNote,
}: StorePaidDetailProps) {
    const live = useLivePayout();
    const liveReady = live.data !== undefined && !live.isError;
    const contributors = contributorsOf(live.data?.sellers ?? [], 'STORE', row.storeId);
    const currentCents = liveReady
        ? contributors.reduce((sum, contributor) => sum + contributor.paidCents, 0)
        : null;
    const place = [row.code, row.zone ?? ''].filter((part) => part !== '').join(' · ');

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-col gap-1 border-b border-border px-4 py-3">
                <div className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                    {place !== '' && <span className="flex-none">{place} ·</span>}
                    <CopyableId inline label="ID" value={row.storeId} className="min-w-0" />
                </div>
                <h2 className="truncate text-[15px] font-semibold">{row.name}</h2>
                <p className="text-[24px] font-semibold tabular-nums">
                    {formatMoneyCents(row.paidCents)}
                </p>
                <ul className="flex flex-wrap gap-1" aria-label="Resumen de la tienda">
                    <li className={CHIP} title={sellersAtLeastTitle(row)}>
                        {sellersChip(row)}
                    </li>
                    <li className={CHIP}>
                        {sharePct(row.paidCents, totalCents).toFixed(1)}% del total · {when}
                    </li>
                </ul>
                <p className="text-[11px] text-muted-foreground">
                    {row.salesCount} ventas · {formatShortMXN(row.placementCents / 100)} colocación
                    en el periodo. Sin los bonos por umbral ni la renta de cartera, que son del
                    vendedor.
                </p>
            </header>
            <SeriesSection
                by="STORE"
                id={row.storeId}
                currentCents={currentCents}
                currentPeriodId={live.data?.periodId}
                currentLoading={live.isLoading}
                title="Comisiones semanales"
            />
            <section className="px-4 py-3">
                <h3 className={SECTION_TITLE}>Vendedores de la tienda esta semana</h3>
                <ContributorsList
                    contributors={contributors}
                    directory={directory}
                    isLoading={live.isLoading}
                    isError={!live.isLoading && !liveReady}
                    note={contributorsNote}
                    label="Vendedores de la tienda esta semana"
                    empty="Nadie vendió en esta tienda esta semana."
                />
            </section>
        </div>
    );
}
